import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { RelationPicker, RelationSelect, StatusBadge } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import { useDistributorScope } from '../hooks/useDistributorScope';
import { usePageActions } from '../contexts/PermissionContext';
import { parseFieldOptions, fieldOptionLabel } from './additionalFieldOptions';
import { sanitizeNumericInput } from '../utils/format';
import { AuditInfo, HistoryButton, SubModal, Icon } from './detail/DetailShell';
import { ChangeHistoryDrawer } from './detail/ChangeHistoryDrawer';

/**
 * ÜRÜN KARTI (/products) — V2 tasarımı.
 * ═══════════════════════════════════════════════════════════════════════════════════
 * V2 prototipi kabul edildi ve ÜRÜNLER menüsünün detay ekranı oldu; eski
 * `ProductDetailPage` ile prototip menüsü (/products-v2) kaldırıldı. İş mantığı eski
 * ekranla BİREBİR AYNIDIR — aynı uçlar, aynı zorunlu alanlar, aynı kaydetme zinciri
 * (Product → ProductUnit → ProductVendor → ProductDeposit → ek alanlar → segmentasyon).
 * Değişen tek şey sunum katmanıdır.
 *
 * Kabuk (breadcrumb + sekmeler + aksiyonlar) DetailShell yerine burada çizilir —
 * tasarımın kendi üst barı ve renkli bölüm kartları var. Kayıt bilgisi (ⓘ) ve
 * değişiklik geçmişi (⟳) yine ORTAK bileşenlerdir; kök `dps` sınıfı yalnızca o iki
 * bileşenin CSS değişkenlerini tanımlamak için durur.
 *
 * Tasarım sabit paletlidir (dark tema token'larına bağlanmaz); prototip kararıdır.
 */

const API = '/api/products';

// ---- V2 tasarım paleti (urun-karti tasarımından) ----
const C = {
  bg: '#F8F8FA',
  card: '#FBFCFF',
  cardBorder: '#E5EBFF',
  line: '#CAD7FA',
  navy: '#282568',
  muted: '#66758C',
  hint: '#8A8A8A',
  placeholder: '#9AA5BB',
  blue: '#0041FF',
  teal: '#0D9BB7',
  purple: '#6C28D7',
  amber: '#FFA927',
  amberInk: '#E09A22',
  amberSoft: '#FBF3DD',
  danger: '#FF4A4A',
  dangerSoft: '#FEF5F6',
  tealSoft: '#E4F7FA',
  blueSoft: '#EBF2FF',
  shadow: '0 6px 24px rgba(30,35,90,0.06)',
};

// Bölüm renkleri: dış çerçeve + başlık + alan çizgisi tek yerden.
const SEC = {
  general: { color: C.blue, soft: C.blueSoft, field: 'rgba(39,99,234,0.49)' },
  group: { color: C.teal, soft: C.tealSoft, field: 'rgba(13,155,183,0.40)' },
  units: { color: C.purple, soft: '#F1EEFF', field: 'rgba(108,40,215,0.36)' },
  tax: { color: C.amber, soft: C.amberSoft, field: 'rgba(255,169,39,0.50)' },
  logistics: { color: C.danger, soft: C.dangerSoft, field: 'rgba(255,74,74,0.80)' },
  media: { color: C.navy, soft: '#CDDFFF', field: '#D1DCFB' },
  deposit: { color: C.purple, soft: '#F1EEFF', field: 'rgba(108,40,215,0.36)' },
  extra: { color: C.teal, soft: C.tealSoft, field: 'rgba(13,155,183,0.40)' },
};

const st = {
  page: { background: C.bg, minHeight: '100%', padding: '18px 22px 40px', fontFamily: "'Poppins', system-ui, -apple-system, sans-serif" },

  topbar: {
    height: 75, background: C.card, border: '1px solid #E2E9FF', borderRadius: 17,
    boxShadow: '0 6px 24px rgba(30,35,90,0.05)', display: 'flex', alignItems: 'center',
    padding: '0 25px', boxSizing: 'border-box', gap: 16, flexWrap: 'wrap',
  },
  crumbMuted: { fontSize: 15, fontWeight: 500, color: C.muted, cursor: 'pointer' },
  crumbCurrent: { fontSize: 15, fontWeight: 600, color: C.navy },
  divider: { width: 1, height: 46, background: `linear-gradient(${C.card}, ${C.navy} 47%, ${C.card})` },

  tabs: { marginTop: 22, display: 'flex', gap: 18, flexWrap: 'wrap' },
  tab: (on) => ({
    minWidth: 180, height: 45, borderRadius: 6, fontSize: 15, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 18px',
    fontFamily: 'inherit',
    ...(on
      ? { background: 'linear-gradient(140deg,#00DFFF,#6C28D7)', color: '#fff', border: 'none', boxShadow: '0 6px 18px rgba(60,50,180,0.16)' }
      : { background: C.card, border: `1px solid ${C.cardBorder}`, color: C.navy, boxShadow: '0 6px 18px rgba(30,35,90,0.05)' }),
  }),

  sectionOuter: (color) => ({ marginTop: 30, background: color, borderRadius: 24, paddingTop: 5, boxShadow: C.shadow }),
  sectionInner: { background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 23, boxSizing: 'border-box', padding: '24px 32px 30px' },
  sectionHead: { display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' },
  sectionIcon: (soft, color) => ({
    width: 38, height: 38, borderRadius: 6, background: soft, color, flex: '0 0 auto',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
  }),
  sectionTitle: (color) => ({ fontSize: 20, fontWeight: 600, color, letterSpacing: -0.2 }),
  sectionSub: (color) => ({ marginTop: 2, fontSize: 12, fontStyle: 'italic', fontWeight: 500, color }),
  sectionNote: {
    marginLeft: 'auto', minHeight: 38, borderRadius: 6, background: C.amberSoft, color: C.amberInk,
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500,
  },
  sectionBody: { marginTop: 24, display: 'flex', flexDirection: 'column', gap: 19 },

  grid: (cols) => ({ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '19px 26px' }),
  field: { display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 },
  label: { fontSize: 12, fontWeight: 500, color: C.muted, letterSpacing: 0.2 },
  req: { color: C.danger, marginLeft: 2 },
  hint: { marginTop: 8, fontSize: 12, fontStyle: 'italic', color: C.hint },
  errText: { marginTop: 5, fontSize: 12, fontWeight: 500, color: C.danger },

  imageBox: (dash) => ({
    height: 164, borderRadius: 12, background: '#F1F6FF', border: `1px dashed ${dash}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  }),
  uploadBtn: {
    marginTop: 12, height: 37, background: '#fff', border: `1px solid ${C.line}`, borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 600,
    color: C.navy, cursor: 'pointer', fontFamily: 'inherit',
  },

  unitRow: { display: 'grid', gridTemplateColumns: 'minmax(150px,1fr) minmax(110px,0.6fr) minmax(200px,1.4fr) minmax(120px,0.7fr) 54px', gap: 18, alignItems: 'end' },
  baseTag: { height: 24, padding: '0 10px', borderRadius: 4, background: '#E7F7EE', color: '#1B8A54', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center' },

  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 14 },
  th: { textAlign: 'left', padding: '0 12px', height: 52, background: C.navy, color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: 0.3 },
  td: { padding: '10px 12px', borderBottom: '1px solid rgba(102,117,140,0.18)', color: '#2F3542' },

  empty: {
    padding: '34px 20px', textAlign: 'center', color: C.muted, fontSize: 14,
    background: '#F4F8FF', borderRadius: 12, border: `1px dashed ${C.line}`,
  },
};

const btnBase = {
  height: 39, padding: '0 18px', borderRadius: 7, fontFamily: 'inherit', fontSize: 15, fontWeight: 600,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9,
};
const btnGhost = { ...btnBase, background: '#fff', border: `1px solid ${C.line}`, color: C.navy };
const btnPrimary = { ...btnBase, background: 'linear-gradient(150deg,#0041FF,#007BFF)', border: 'none', color: '#fff' };
const btnDanger = { ...btnBase, height: 34, padding: '0 12px', fontSize: 13, background: C.dangerSoft, border: `1px solid ${C.danger}`, color: C.danger };

// Tasarımın Poppins tipografisi + ortak ⓘ/⟳ butonlarının V2 kabuğundaki görünümü.
const V2_FONT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
.prd2 input::placeholder, .prd2 textarea::placeholder { color: ${C.placeholder}; }
.prd2 .dps-audit-btn, .prd2 .dps-hist-btn {
  width: 37px; height: 32px; border-radius: 6px; background: #fff; border: 1px solid ${C.line};
  display: inline-flex; align-items: center; justify-content: center; color: ${C.navy}; cursor: pointer;
}
.prd2 .dps-audit { position: relative; display: inline-flex; }
`;

// Alan kutusu (tasarımın beyaz zemin + renkli çerçeve dili).
const boxStyle = (accent, invalid) => ({
  height: 38, padding: '0 15px', borderRadius: 6, background: '#fff',
  border: `1px solid ${invalid ? C.danger : accent}`, color: C.navy, fontSize: 14, fontWeight: 600,
  fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none',
});

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
};
const STATUS_OPTIONS = [{ value: 1, label: 'Aktif' }, { value: 0, label: 'Pasif' }, { value: 2, label: 'Beklemede' }];
const APPROVAL_OPTIONS = ['Onaylandı', 'Onay Bekliyor', 'Reddedildi'];
const APPAREA_OPTIONS = ['Hepsi', 'Saha Satış', 'Telesatış', 'B2B'];
const PRODUCTTYPE_OPTIONS = ['Normal', 'Promosyon', 'Hediye', 'Sarf', 'Depozito'];
const STOCK_TYPE_DEPOSIT = 2; // StockType enum: 0=Satılabilir,1=Bedelsiz,2=Depozito,3=Hasarlı
const DEFAULT_UNIT_OPTIONS = [{ value: 1, label: 'Birim 1' }, { value: 2, label: 'Birim 2' }, { value: 3, label: 'Birim 3' }];

const SEGMENTATION_COLUMNS = [
  { key: 'treeName', header: 'Ağaç', width: '28%' },
  { key: 'path', header: 'Kategori Yolu' },
  { key: 'name', header: 'Kategori', width: '24%' },
  { key: 'level', header: 'Seviye', type: 'number', width: 90, align: 'right' },
];

const EMPTY = {
  textId: '', name: '', shortName: '', description: '', trackingCode: '', prefix: '',
  applicationArea: 'Hepsi', approvalStatus: 'Onaylandı', status: 1,
  groupId: '', subGroupId: '', producerId: '', vendorId: '',
  vendorIds: [],
  level1ProductId: '', level2ProductId: '', dynamicGroupId: '',
  centerCode: '', specialCode: '', countryCode: '',
  salesVatPercentage: '', purchaseVatPercentage: '', productType: 'Normal', unit: 1,
  maxDiscountRate: '', maxDiscountAmount: '', alcoholRate: '', gtip: '',
  weight: '', volume: '', liter: '', width: '', lenght: '', height: '', caseQuantity: '',
  minOrderQuantity: '',
  productImage: '', videoUrl: '', additionalImage1: '', additionalImage2: '', mobileSortOrder: '',
  defaultStockType: 0,
  segmentationNodeId: '',
};

// Prefix'siz/prefix'li base64'ten görüntülenebilir data-URL üretir.
function toImgSrc(v) {
  if (!v) return '';
  if (v.startsWith('data:')) return v;
  const mime = v.startsWith('/9j/') ? 'image/jpeg'
    : v.startsWith('R0lGOD') ? 'image/gif'
    : v.startsWith('UklGR') ? 'image/webp'
    : 'image/png';
  return `data:${mime};base64,${v}`;
}
const stripImg = (v) => (v ? (v.includes(',') ? v.split(',')[1] : v) : null);

const numStr = (n) => (n === null || n === undefined || n === '' ? '' : String(n));
const num = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const unwrap = (b) => (Array.isArray(b) ? b : (b?.data ?? b?.Data ?? b?.value ?? []));
const unwrapEntity = (b) => (b?.data ?? b?.Data ?? b?.value ?? b);

// Görsel yükleme kutucuğu (Master / Ek Görsel) — tasarımın dashed dropzone dili.
function ImageBox({ label, value, onPick, onClear, readOnly, accent }) {
  const src = toImgSrc(value);
  return (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <div style={st.imageBox(accent)}>
        {src
          ? <img src={src} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <span style={{ fontSize: 13, color: '#5B84E8' }}>Görsel yok</span>}
      </div>
      {!readOnly && (
        <div style={{ display: 'flex', gap: 10 }}>
          <label style={{ ...st.uploadBtn, flex: 1 }}>
            {value ? 'Değiştir' : 'Yükle'}
            <input type="file" accept="image/*" onChange={onPick} style={{ display: 'none' }} />
          </label>
          {value && (
            <button type="button" onClick={onClear} style={{ ...st.uploadBtn, flex: '0 0 auto', marginTop: 12, padding: '0 14px', borderColor: C.danger, color: C.danger }}>
              Kaldır
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Distribütör çoklu seçim — chip'ler + filtrelenebilir açılır liste. Boş = tüm distribütörler.
function MultiVendorSelect({ value = [], options = [], onChange, readOnly, accent }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const selected = new Set(value.map(String));
  const labelOf = (vid) => options.find((o) => String(o.id) === String(vid))?.label || vid;
  const ql = q.toLocaleLowerCase('tr');
  const filtered = options.filter((o) => !selected.has(String(o.id)) && (!ql || o.label.toLocaleLowerCase('tr').includes(ql)));
  const add = (vid) => { onChange([...value, vid]); setQ(''); };
  const remove = (vid) => onChange(value.filter((v) => String(v) !== String(vid)));
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: value.length ? 8 : 0 }}>
        {value.map((vid) => (
          <span key={vid} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, fontSize: 13, fontWeight: 600, background: C.blueSoft, color: C.blue, border: `1px solid ${accent}` }}>
            {labelOf(vid)}
            {!readOnly && (
              <button type="button" onClick={() => remove(vid)} title="Kaldır" style={{ border: 'none', background: 'transparent', color: C.blue, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
            )}
          </span>
        ))}
      </div>
      {readOnly ? (
        value.length === 0 && <div style={{ ...boxStyle(accent), display: 'flex', alignItems: 'center', color: C.placeholder, fontWeight: 500 }}>Tüm distribütörler</div>
      ) : (
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Distribütör ekle…"
          style={{ ...boxStyle(accent), background: '#ECF3FF', fontWeight: 500 }}
        />
      )}
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: 4, maxHeight: 220, overflowY: 'auto', background: '#fff', border: `1px solid ${accent}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.14)' }}>
          {filtered.slice(0, 50).map((o) => (
            <div
              key={o.id} onMouseDown={() => add(o.id)}
              style={{ padding: '9px 12px', fontSize: 14, cursor: 'pointer', color: C.navy }}
              onMouseEnter={(e) => { e.currentTarget.style.background = C.blueSoft; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Renkli bölüm kartı — dış çerçeve rengi bölümün kimliğidir.
function SectionCard({ tone, icon, title, subtitle, note, right, children }) {
  const t = SEC[tone] || SEC.general;
  return (
    <div style={st.sectionOuter(t.color)}>
      <div style={st.sectionInner}>
        <div style={st.sectionHead}>
          <div style={st.sectionIcon(t.soft, t.color)}>{icon}</div>
          <div>
            <div style={st.sectionTitle(t.color)}>{title}</div>
            {subtitle && <div style={st.sectionSub(t.color)}>{subtitle}</div>}
          </div>
          {note && <div style={st.sectionNote}>ⓘ {note}</div>}
          {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
        </div>
        <div style={st.sectionBody}>{children}</div>
      </div>
    </div>
  );
}

export function ProductDetailV2Page() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = !id;
  usePageMeta('Ürün Kartı (V2)', 'V2 tasarım prototipi');
  const { canAdd, canEdit } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  // #282 deseni: mevcut kayıt düzenleme niyeti (?edit=1) gelmedikçe İZLEME modunda açılır;
  // niyet Update hakkını yenemez.
  const editIntent = searchParams.get('edit') === '1';
  const isView = !isNew && (!editIntent || !canEdit);

  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [units, setUnits] = useState([{ unitName: '', multiplier: '', barcode: '', maxWeight: '', isBase: true }]);
  const [labels, setLabels] = useState({});
  const [tab, setTab] = useState('Ürün');
  const [tried, setTried] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [deposits, setDeposits] = useState([]);
  const [depositProducts, setDepositProducts] = useState([]);
  const [depositDraft, setDepositDraft] = useState({ depositProductId: '', quantity: '1' });
  const [segmentationLeafNodes, setSegmentationLeafNodes] = useState([]);
  const [audit, setAudit] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const { lockedVendorId, scopeLoaded } = useDistributorScope();
  const [memberGroupIds, setMemberGroupIds] = useState([]);
  const [allDynamicGroups, setAllDynamicGroups] = useState([]);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDraft, setMemberDraft] = useState([]);
  const [memberSaving, setMemberSaving] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3000); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const startEditing = () => {
    const sp = new URLSearchParams(searchParams);
    sp.set('edit', '1');
    setSearchParams(sp, { replace: true });
  };

  // Özelleştirilmiş grup (havuz) üyelikleri.
  const memberCodes = memberGroupIds
    .map((gid) => allDynamicGroups.find((g) => g.id === gid)?.textId)
    .filter(Boolean)
    .join(', ');
  const openMemberModal = () => { setMemberDraft(memberGroupIds); setMemberSearch(''); setMemberModalOpen(true); };
  const toggleMemberDraft = (gid) => setMemberDraft((cur) => (cur.includes(gid) ? cur.filter((x) => x !== gid) : [...cur, gid]));
  const applyMemberships = async () => {
    if (isNew) return;
    if (memberDraft.length > 10) { showToast('error', 'Bir ürün en fazla 10 özelleştirilmiş gruba eklenebilir'); return; }
    setMemberSaving(true);
    try {
      const res = await fetch('/api/productdynamicgroups/SetProductMemberships', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ productId: id, groupIds: memberDraft }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Üyelik güncellenemedi');
      setMemberGroupIds(memberDraft);
      setMemberModalOpen(false);
      showToast('success', 'Özelleştirilmiş grup üyelikleri güncellendi');
    } catch (e) {
      showToast('error', e.message || 'Üyelik güncellenirken hata oluştu');
    } finally {
      setMemberSaving(false);
    }
  };

  // Depozito satırı ekle/çıkar + etiket.
  const depositLabel = (dpid) => {
    const p = depositProducts.find((x) => x.id === dpid);
    return p ? (p.name ? `${p.textId} - ${p.name}` : p.textId) : dpid;
  };
  const addDeposit = () => {
    const dpid = depositDraft.depositProductId;
    const qty = num(depositDraft.quantity);
    if (!dpid) { showToast('error', 'Depozito ürünü seçin'); return; }
    if (!qty || qty <= 0) { showToast('error', 'Miktar 0’dan büyük olmalı'); return; }
    if (deposits.some((d) => d.depositProductId === dpid)) { showToast('error', 'Bu depozito ürünü zaten ekli'); return; }
    setDeposits((c) => [...c, { depositProductId: dpid, quantity: String(qty) }]);
    setDepositDraft({ depositProductId: '', quantity: '1' });
  };
  const setDepositQty = (dpid, v) => setDeposits((c) => c.map((d) => (d.depositProductId === dpid ? { ...d, quantity: v } : d)));
  const removeDeposit = (dpid) => setDeposits((c) => c.filter((d) => d.depositProductId !== dpid));

  // Görsel seç → data-URL olarak forma yaz (kaydederken stripImg ile prefix ayıklanır).
  const pickImage = (key) => (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { showToast('error', "Görsel 2 MB'dan küçük olmalı"); return; }
    const reader = new FileReader();
    reader.onload = () => set(key, reader.result);
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const vendorOptions = useMemo(
    () => Object.entries(labels.vendor || {}).map(([vid, label]) => ({ id: vid, label: String(label) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'tr')),
    [labels.vendor],
  );

  const buildLabels = useCallback(async () => {
    const headers = authHeaders();
    const fetchSel = (url, fmt) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then((b) => {
      const map = {};
      unwrap(b).forEach((x) => { map[x.id] = fmt(x); });
      return map;
    }).catch(() => ({}));
    const named = (x) => (x.name ? `${x.textId || ''} - ${x.name}`.replace(/^ - /, '') : (x.textId || x.id));
    const desc = (x) => (x.description ? `${x.textId || ''} - ${x.description}`.replace(/^ - /, '') : (x.textId || x.id));
    const [g, sg, pr, vn, dg] = await Promise.all([
      fetchSel('/api/productgroups/Select', desc),
      fetchSel('/api/productsubgroups/Select', desc),
      fetchSel('/api/producers/Select', named),
      fetchSel('/api/vendors/Select', named),
      fetchSel('/api/productdynamicgroups/Select', desc),
    ]);
    setLabels({ group: g, subGroup: sg, producer: pr, vendor: vn, dynamicGroup: dg, _ready: true });
  }, []);

  const mapToForm = useCallback((l) => ({
    textId: l.textId || '', name: l.name || '', shortName: l.shortName || '', description: l.description || '',
    trackingCode: l.trackingCode || '', prefix: l.prefix || '',
    applicationArea: l.applicationArea || 'Hepsi', approvalStatus: l.approvalStatus || 'Onaylandı', status: l.status ?? 1,
    groupId: l.groupId || '', subGroupId: l.subGroupId || '', producerId: l.producerId || '', vendorId: l.vendorId || '',
    level1ProductId: l.level1ProductId || '', level2ProductId: l.level2ProductId || '', dynamicGroupId: l.dynamicGroupId || '',
    centerCode: l.centerCode || '', specialCode: l.specialCode || '', countryCode: l.countryCode || '',
    salesVatPercentage: numStr(l.salesVatPercentage), purchaseVatPercentage: numStr(l.purchaseVatPercentage),
    productType: l.productType || 'Normal', unit: l.unit ?? 1,
    maxDiscountRate: numStr(l.maxDiscountRate), maxDiscountAmount: numStr(l.maxDiscountAmount),
    alcoholRate: numStr(l.alcoholRate), gtip: l.gtip || '',
    weight: numStr(l.weight), volume: numStr(l.volume), liter: numStr(l.liter),
    width: numStr(l.width), lenght: numStr(l.lenght), height: numStr(l.height), caseQuantity: numStr(l.caseQuantity),
    minOrderQuantity: numStr(l.minOrderQuantity),
    productImage: l.productImage || '', videoUrl: l.videoUrl || '',
    additionalImage1: l.additionalImage1 || '', additionalImage2: l.additionalImage2 || '',
    mobileSortOrder: numStr(l.mobileSortOrder),
    vendorIds: [],
    defaultStockType: Number(l.defaultStockType ?? 0),
    segmentationNodeId: l.segmentationNodeId || '',
  }), []);

  useEffect(() => {
    let alive = true;
    buildLabels();
    fetch('/api/productadditionalfields/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : [])).then((b) => { if (alive) setFieldDefs(unwrap(b)); }).catch(() => {});
    fetch('/api/productdynamicgroups/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : [])).then((b) => {
        if (!alive) return;
        setAllDynamicGroups((unwrap(b) || []).map((g) => ({
          id: g.id ?? g.Id, textId: g.textId ?? g.TextId ?? '', description: g.description ?? g.Description ?? '',
        })));
      }).catch(() => {});
    fetch(`/api/products?$top=500&$orderby=TextId&$filter=${encodeURIComponent("ProductType eq 'Depozito'")}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null)).then((b) => {
        if (!alive) return;
        const rows = b?.value ?? b?.Value ?? [];
        setDepositProducts(rows.map((p) => ({ id: p.Id ?? p.id, textId: p.TextId ?? p.textId, name: p.Name ?? p.name })));
      }).catch(() => {});
    fetch('/api/productSegmentationTree/leaf-nodes', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (alive) setSegmentationLeafNodes(unwrap(data) || []); })
      .catch(() => {});
    if (isNew) { setLoading(false); return () => { alive = false; }; }
    setLoading(true);
    // Her yardımcı fetch KENDİ hatasını yutar (204/boş gövde → fallback; Promise.all kırılmasın).
    const safeJson = (r, fallback) => ((r.ok && r.status !== 204) ? r.json().catch(() => fallback) : fallback);
    Promise.all([
      fetch(`${API}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() }).then((r) => safeJson(r, null)).catch(() => null),
      fetch(`/api/productunits/ForProduct?productId=${encodeURIComponent(id)}`, { headers: authHeaders() }).then((r) => safeJson(r, [])).catch(() => []),
      fetch(`/api/productadditionalfields/ValuesForProduct?productId=${encodeURIComponent(id)}`, { headers: authHeaders() }).then((r) => safeJson(r, [])).catch(() => []),
      fetch(`/api/productSegmentationTree/node-for-product/${encodeURIComponent(id)}`, { headers: authHeaders() }).then((r) => safeJson(r, null)).catch(() => null),
      fetch(`/api/productvendors/ForProduct?productId=${encodeURIComponent(id)}`, { headers: authHeaders() }).then((r) => safeJson(r, [])).catch(() => []),
      fetch(`/api/productdynamicgroups/ForProduct?productId=${encodeURIComponent(id)}`, { headers: authHeaders() }).then((r) => safeJson(r, [])).catch(() => []),
      fetch(`/api/productdeposits/ForProduct?productId=${encodeURIComponent(id)}`, { headers: authHeaders() }).then((r) => safeJson(r, [])).catch(() => []),
    ]).then(([pJson, uJson, vJson, nodeJson, pvJson, dgJson, depJson]) => {
      if (!alive) return;
      const l = unwrapEntity(pJson);
      if (l) {
        setLoaded(l); setForm(mapToForm(l));
        setAudit({ createdBy: l.createdBy ?? l.CreatedBy, createdDate: l.createdDate ?? l.CreatedDate, updatedBy: l.updatedBy ?? l.UpdatedBy, updatedDate: l.updatedDate ?? l.UpdatedDate });
      }
      const linkedNodeId = nodeJson?.nodeId ?? nodeJson?.NodeId ?? '';
      if (linkedNodeId) setForm((p) => ({ ...p, segmentationNodeId: linkedNodeId }));
      const vendorIds = (unwrap(pvJson) || []).map((x) => (typeof x === 'string' ? x : (x.vendorId ?? x.VendorId ?? x))).filter(Boolean);
      if (vendorIds.length) setForm((p) => ({ ...p, vendorIds }));
      setMemberGroupIds((unwrap(dgJson) || []).map((g) => g.id ?? g.Id).filter(Boolean));
      setDeposits((unwrap(depJson) || []).map((d) => ({
        depositProductId: d.depositProductId ?? d.DepositProductId,
        quantity: numStr(d.quantity ?? d.Quantity),
      })).filter((d) => d.depositProductId));
      const us = unwrap(uJson).map((u) => ({ unitName: u.unitName ?? u.UnitName ?? '', multiplier: numStr(u.multiplier ?? u.Multiplier), barcode: u.barcode ?? u.Barcode ?? '', maxWeight: numStr(u.maxWeight ?? u.MaxWeight), isBase: !!(u.isBase ?? u.IsBase) }));
      setUnits(us.length ? us : [{ unitName: l?.unit1 || 'Adet', multiplier: '', barcode: '', maxWeight: '', isBase: true }]);
      const vmap = {};
      unwrap(vJson).forEach((it) => { vmap[it.additionalFieldId ?? it.AdditionalFieldId] = it.value ?? it.Value ?? ''; });
      setFieldValues(vmap);
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, isNew, buildLabels, mapToForm]);

  // #754 Distribütör tipi kullanıcı yeni ürün açtığında kendi distribütörü seçili gelir.
  useEffect(() => {
    if (!isNew || !scopeLoaded || !lockedVendorId) return;
    setForm((c) => (c.vendorIds && c.vendorIds.length ? c : { ...c, vendorIds: [lockedVendorId] }));
  }, [isNew, scopeLoaded, lockedVendorId]);

  // birim satır işlemleri
  const setUnit = (i, k, v) => setUnits((u) => u.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addUnit = () => setUnits((u) => [...u, { unitName: '', multiplier: '', barcode: '', maxWeight: '', isBase: false }]);
  const delUnit = (i) => setUnits((u) => (i === 0 ? u : u.filter((_, idx) => idx !== i)));

  // #729 Web ürün kartının zorunlu alanları (backend geriye uyum için yalnız Ad'ı zorunlu tutar).
  const REQ = ['name', 'textId', 'shortName', 'groupId', 'subGroupId', 'producerId', 'salesVatPercentage', 'purchaseVatPercentage', 'weight'];
  const REQ_LABELS = {
    name: 'Ad', textId: 'Ürün Kodu', shortName: 'Ürün Kısa Adı', groupId: 'Ürün Grubu',
    subGroupId: 'Ürün Alt Grubu', producerId: 'Üretici', salesVatPercentage: 'Satış KDV Oranı',
    purchaseVatPercentage: 'Alış KDV Oranı', weight: 'Ağırlık (kg)',
  };
  const isMissing = (k) => tried && REQ.includes(k) && !String(form[k] ?? '').trim();

  const handleSave = async () => {
    setTried(true);
    const missing = REQ.filter((k) => !String(form[k] ?? '').trim());
    if (missing.length) {
      // Zorunlu alanların hepsi "Ürün" sekmesindedir — kullanıcıyı oraya döndür ve eksikleri say.
      setTab('Ürün');
      showToast('error', `Zorunlu alan(lar) eksik: ${missing.map((k) => REQ_LABELS[k] || k).join(', ')}`);
      return;
    }
    const missingCustom = fieldDefs.filter((f) => f.isRequired && !String(fieldValues[f.id] ?? '').trim());
    if (missingCustom.length) {
      setTab('Ek Bilgiler');
      showToast('error', `Zorunlu ek bilgi alan(lar)ı eksik: ${missingCustom.map((f) => f.name || f.code || f.id).join(', ')}`);
      return;
    }
    setSaving(true);
    const pid = isNew ? (window.crypto?.randomUUID ? window.crypto.randomUUID() : undefined) : id;
    const baseUnit = units.find((u) => String(u.unitName).trim()) || units[0];
    // Tam-değiştirme (EfRepository.Update Attach+Modified) — silinmesin diye loaded alanlarını echo'la.
    const payload = {
      id: pid,
      textId: form.textId, name: form.name,
      shortName: form.shortName || null, description: form.description || null,
      erpId: loaded?.erpId ?? null,
      status: Number(form.status) || 0,
      unit: Number(form.unit) || 1,
      unit1: (baseUnit?.unitName || loaded?.unit1 || 'Adet'),
      unit2: loaded?.unit2 ?? null, unit2Multiplier: loaded?.unit2Multiplier ?? null,
      unit3: loaded?.unit3 ?? null, unit3Multiplier: loaded?.unit3Multiplier ?? null,
      unit4: loaded?.unit4 ?? null, unit4Multiplier: loaded?.unit4Multiplier ?? null,
      unit5: loaded?.unit5 ?? null, unit5Multiplier: loaded?.unit5Multiplier ?? null,
      groupId: form.groupId || null, subGroupId: form.subGroupId || null,
      producerId: form.producerId || null,
      vendorId: (form.vendorIds && form.vendorIds.length ? form.vendorIds[0] : null),
      hierarchyId: loaded?.hierarchyId ?? null, productWarehouseId: loaded?.productWarehouseId ?? null,
      additionalDescription1: loaded?.additionalDescription1 ?? null,
      additionalDescription2: loaded?.additionalDescription2 ?? null,
      salesVatPercentage: num(form.salesVatPercentage) ?? 0,
      purchaseVatPercentage: num(form.purchaseVatPercentage) ?? 0,
      width: num(form.width), lenght: num(form.lenght), height: num(form.height),
      productImage: stripImg(form.productImage),
      additionalImage1: stripImg(form.additionalImage1),
      additionalImage2: stripImg(form.additionalImage2),
      videoUrl: form.videoUrl ? String(form.videoUrl).trim() : null,
      mobileSortOrder: form.mobileSortOrder === '' || form.mobileSortOrder === null || form.mobileSortOrder === undefined
        ? null : (Number.isFinite(parseInt(form.mobileSortOrder, 10)) ? parseInt(form.mobileSortOrder, 10) : null),
      timestamp: loaded?.timestamp ?? null,
      trackingCode: form.trackingCode || null, prefix: form.prefix || null,
      applicationArea: form.applicationArea || null, approvalStatus: form.approvalStatus || null,
      centerCode: form.centerCode || null, specialCode: form.specialCode || null, countryCode: form.countryCode || null,
      productType: form.productType || null, gtip: form.gtip || null,
      alcoholRate: num(form.alcoholRate), maxDiscountRate: num(form.maxDiscountRate), maxDiscountAmount: num(form.maxDiscountAmount),
      weight: num(form.weight), volume: num(form.volume), liter: num(form.liter), caseQuantity: num(form.caseQuantity),
      minOrderQuantity: num(form.minOrderQuantity),
      defaultStockType: form.productType === 'Depozito' ? STOCK_TYPE_DEPOSIT : (Number(form.defaultStockType) || 0),
      level1ProductId: form.level1ProductId || null, level2ProductId: form.level2ProductId || null,
      dynamicGroupId: form.dynamicGroupId || null,
    };
    try {
      const res = await fetch(`${API}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt başarısız');

      // Birimler — backend Product.Unit1..5 senkronu + doğrulama yapar (>5 birim / çarpansız üst birim → 400).
      const unitsRes = await fetch('/api/productunits/SaveForProduct', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          productId: pid,
          units: units.filter((u) => String(u.unitName).trim()).map((u, i) => ({
            unitName: u.unitName, multiplier: num(u.multiplier), barcode: u.barcode || null,
            maxWeight: num(u.maxWeight), isBase: i === 0, sortOrder: i,
          })),
        }),
      });
      const unitsBody = await unitsRes.json().catch(() => null);
      if (!unitsRes.ok) throw new Error(unitsBody?.message || unitsBody?.Message || 'Birimler kaydedilemedi');
      await fetch('/api/productvendors/SaveForProduct', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ productId: pid, vendorIds: (form.vendorIds || []).filter(Boolean) }),
      }).catch(() => {});
      await fetch('/api/productdeposits/SaveForProduct', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          productId: pid,
          items: (deposits || [])
            .filter((d) => d.depositProductId && num(d.quantity) > 0)
            .map((d) => ({ depositProductId: d.depositProductId, quantity: num(d.quantity) })),
        }),
      }).catch(() => {});
      if (fieldDefs.length) {
        await fetch('/api/productadditionalfields/SaveValues', {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ productId: pid, values: fieldDefs.map((f) => ({ additionalFieldId: f.id, value: String(fieldValues[f.id] ?? '') })) }),
        });
      }
      await fetch('/api/productSegmentationTree/set-product-node', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ productId: pid, nodeId: form.segmentationNodeId || null }),
      }).catch(() => {}); // Bağlama hatası ürün kaydını kırmaz
      showToast('success', 'Ürün kartı kaydedildi');
      setTimeout(() => navigate('/products'), 500);
    } catch (e) {
      showToast('error', e.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const requiredFieldsMissing = useMemo(() => fieldDefs.filter((f) => f.isRequired).map((f) => f.id), [fieldDefs]);
  const statusMeta = STATUS_META[Number(form.status)];

  // ---- Alan render yardımcıları (JSX bileşeni DEĞİL — odak kaybı olmasın) ----
  const TextField = ({ label, k, tone = 'general', req = false, suffix = '', placeholder = '', readOnly = false, numeric = false, span = 1 }) => {
    const accent = SEC[tone].field;
    const invalid = isMissing(k);
    const ro = isView || readOnly;
    return (
      <div key={k} style={{ ...st.field, gridColumn: span > 1 ? `span ${span}` : undefined }}>
        <label style={st.label}>{label}{req && <span style={st.req}>*</span>}</label>
        <div style={{ position: 'relative' }}>
          <input
            style={{ ...boxStyle(accent, invalid), paddingRight: suffix ? 34 : 15, background: ro ? '#F7F9FF' : '#fff' }}
            value={form[k] ?? ''} readOnly={ro} placeholder={placeholder}
            inputMode={numeric ? 'decimal' : undefined}
            onChange={(e) => set(k, numeric ? sanitizeNumericInput(e.target.value) : e.target.value)}
          />
          {suffix && <span style={{ position: 'absolute', right: 14, top: 10, color: C.placeholder, fontWeight: 500 }}>{suffix}</span>}
        </div>
        {invalid && <div style={st.errText}>Zorunlu alan</div>}
      </div>
    );
  };

  const SelectFieldV2 = ({ label, k, options, tone = 'general', req = false, span = 1 }) => {
    const accent = SEC[tone].field;
    const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
    const invalid = isMissing(k);
    return (
      <div key={k} style={{ ...st.field, gridColumn: span > 1 ? `span ${span}` : undefined }}>
        <label style={st.label}>{label}{req && <span style={st.req}>*</span>}</label>
        {isView ? (
          <input style={{ ...boxStyle(accent, invalid), background: '#F7F9FF' }} readOnly value={opts.find((o) => String(o.value) === String(form[k]))?.label ?? (form[k] ?? '-')} />
        ) : (
          <select style={boxStyle(accent, invalid)} value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)}>
            {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
      </div>
    );
  };

  const LookupField = ({ label, k, entity, config, tone = 'group', req = false, span = 1 }) => {
    const accent = SEC[tone].field;
    const invalid = isMissing(k);
    const shown = form[k] ? (labels[entity]?.[form[k]] || '…') : '';
    return (
      <div key={k} style={{ ...st.field, gridColumn: span > 1 ? `span ${span}` : undefined }}>
        <label style={st.label}>{label}{req && <span style={st.req}>*</span>}</label>
        {isView ? (
          <input style={{ ...boxStyle(accent, invalid), background: '#F7F9FF' }} readOnly value={shown || '-'} />
        ) : (
          <RelationPicker
            config={config}
            value={form[k]}
            selectedLabel={shown}
            onChange={(val) => set(k, val || '')}
            placeholder="Seçiniz"
          />
        )}
        {invalid && <div style={st.errText}>Zorunlu alan</div>}
      </div>
    );
  };

  if (loading) {
    return <div className="dps prd2" style={{ ...st.page, color: C.muted }}><style>{V2_FONT_CSS}</style>Yükleniyor…</div>;
  }

  return (
    <div className="dps prd2" style={st.page}>
      <style>{V2_FONT_CSS}</style>

      {/* ① ÜST BAR */}
      <div style={st.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={st.crumbMuted} onClick={() => navigate('/products')}>Ürünler</span>
          <span style={{ color: C.muted }}>❯</span>
          <span style={st.crumbCurrent}>{isNew ? 'Yeni Ürün' : 'Ürün Kartı'}</span>
          {!isNew && statusMeta && <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>}
          {isView && !isNew && (
            <span style={{ fontSize: 13, fontWeight: 600, color: C.teal, background: C.tealSoft, borderRadius: 4, padding: '5px 10px' }}>İzleme</span>
          )}
          {!isNew && (
            <>
              <div style={st.divider} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {audit && <AuditInfo audit={audit} />}
                <HistoryButton onClick={() => setHistOpen(true)} />
              </div>
            </>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {isView ? (
            <>
              {canEdit && <button type="button" style={btnPrimary} onClick={startEditing}>✎ Düzenle</button>}
              <button type="button" style={btnGhost} onClick={() => navigate('/products')}>Kapat</button>
            </>
          ) : (
            <>
              <button type="button" style={btnGhost} onClick={() => navigate('/products')} disabled={saving}>İptal</button>
              {canSave && (
                <button type="button" style={btnPrimary} onClick={handleSave} disabled={saving}>
                  {saving ? 'Kaydediliyor…' : '✓ Kaydet'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {!isNew && (
        <ChangeHistoryDrawer
          open={histOpen}
          onClose={() => setHistOpen(false)}
          entityName="Product"
          entityId={id}
          audit={audit}
        />
      )}

      {/* ② SEKMELER */}
      <div style={st.tabs}>
        {['Ürün', 'Depozitolar', 'Ek Bilgiler'].map((t) => (
          <button key={t} type="button" style={st.tab(tab === t)} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {/* ── ÜRÜN ── */}
      {tab === 'Ürün' && (
        <>
          <SectionCard tone="general" icon="📄" title="Genel Bilgiler" subtitle="Ürünün kimlik ve durum bilgileri">
            <div style={{ display: 'flex', gap: 36, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 460px', display: 'flex', flexDirection: 'column', gap: 19, minWidth: 0 }}>
                <div style={st.field}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
                    <label style={st.label}>DİSTRİBÜTÖR (ÇOKLU)</label>
                    <span style={{ fontSize: 12, fontStyle: 'italic', color: C.hint }}>Boş - tüm distribütörler bu ürünü kullanabilir</span>
                  </div>
                  <MultiVendorSelect
                    value={form.vendorIds} options={vendorOptions}
                    onChange={(vids) => set('vendorIds', vids)}
                    readOnly={isView} accent={SEC.general.field}
                  />
                </div>
                {TextField({ label: 'AD', k: 'name', req: true })}
              </div>
              {/* Tasarımdaki büyük görsel kutusu: master görselin ÖNİZLEMESİ. Yükleme tek yerden
                  (Medya bölümü) yapılır — iki ayrı yükleme noktası aynı alanı yönetirse hangisinin
                  kazandığı kullanıcıya görünmez olurdu. */}
              <div style={{ flex: '0 0 332px', maxWidth: '100%' }}>
                <label style={st.label}>ÜRÜN GÖRSELİ</label>
                <div style={{ ...st.imageBox(C.blue), marginTop: 5 }}>
                  {form.productImage
                    ? <img src={toImgSrc(form.productImage)} alt="Ürün" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <span style={{ fontSize: 13, color: '#5B84E8' }}>Görsel yok</span>}
                </div>
                <div style={st.hint}>Görseli “Medya” bölümünden yükleyin.</div>
              </div>
            </div>

            <div style={st.grid(2)}>
              {SelectFieldV2({ label: 'ÜRÜN TİPİ', k: 'productType', options: PRODUCTTYPE_OPTIONS })}
              {TextField({ label: 'ÜRÜN KISA ADI', k: 'shortName', req: true })}
              {TextField({ label: 'ÜRÜN AÇIKLAMASI', k: 'description' })}
              {SelectFieldV2({ label: 'UYGULAMA YERİ', k: 'applicationArea', options: APPAREA_OPTIONS })}
            </div>
            <div style={st.grid(3)}>
              {TextField({ label: 'ÜRÜN KODU', k: 'textId', req: true })}
              {SelectFieldV2({ label: 'ONAY', k: 'approvalStatus', options: APPROVAL_OPTIONS })}
              {SelectFieldV2({ label: 'DURUM', k: 'status', options: STATUS_OPTIONS })}
            </div>
          </SectionCard>

          <SectionCard tone="group" icon="▦" title="Gruplama" subtitle="Ürün grup ve sınıflandırma kodları">
            <div style={st.grid(2)}>
              {LookupField({ label: 'ÜRÜN GRUBU', k: 'groupId', entity: 'group', config: RELATION_CONFIGS.productGroup, req: true })}
              {LookupField({ label: 'ÜRÜN ALT GRUBU', k: 'subGroupId', entity: 'subGroup', config: RELATION_CONFIGS.productSubGroup, req: true })}
              {LookupField({ label: 'ÜRETİCİ', k: 'producerId', entity: 'producer', config: RELATION_CONFIGS.producer, req: true })}
              {LookupField({ label: 'DİNAMİK GRUP (ÖZELLEŞTİRİLMİŞ)', k: 'dynamicGroupId', entity: 'dynamicGroup', config: RELATION_CONFIGS.productDynamicGroup })}
            </div>
            <div style={st.field}>
              <label style={st.label}>AİT OLDUĞU ÖZELLEŞTİRİLMİŞ GRUPLAR (HAVUZLAR)</label>
              <button
                type="button"
                onClick={isNew || isView ? undefined : openMemberModal}
                disabled={isNew || isView}
                style={{
                  ...boxStyle(SEC.group.field), background: '#F8FEFD', textAlign: 'left',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  cursor: isNew || isView ? 'default' : 'pointer',
                  color: memberCodes ? C.navy : C.placeholder, fontWeight: memberCodes ? 600 : 500,
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {isNew ? 'Önce ürünü kaydedin' : (memberCodes || (isView ? 'Havuz yok' : 'Havuz seçmek için tıklayın'))}
                </span>
                {!isNew && !isView && <Icon name="edit" size={15} />}
              </button>
              <div style={st.hint}>Ürün en fazla 10 özelleştirilmiş grup havuzuna eklenebilir. Kodları virgülle gösterilir; düzenlemek için tıklayın.</div>
            </div>
            <div style={st.field}>
              <label style={st.label}>SEGMENTASYON KATEGORİSİ</label>
              {isView ? (
                <input
                  style={{ ...boxStyle(SEC.group.field), background: '#F7F9FF' }} readOnly
                  value={(() => {
                    const n = segmentationLeafNodes.find((x) => String(x.id ?? x.Id) === String(form.segmentationNodeId));
                    return n ? `${n.treeName ? `${n.treeName} › ` : ''}${n.path || n.name}` : '-';
                  })()}
                />
              ) : (
                <RelationSelect
                  rows={segmentationLeafNodes}
                  value={form.segmentationNodeId || ''}
                  onChange={(val) => set('segmentationNodeId', val || '')}
                  columns={SEGMENTATION_COLUMNS}
                  getRowLabel={(n) => `${n.treeName ? `${n.treeName} › ` : ''}${n.path || n.name}`}
                  searchKeys={['treeName', 'path', 'name']}
                  title="Segmentasyon Kategorisi"
                  placeholder="— Kategori Seçin —"
                  searchPlaceholder="Ağaç veya kategori adı ara…"
                  panelMinWidth={620}
                />
              )}
              <div style={st.hint}>Ürünü hiyerarşik ağacın en uç (yaprak) seçeneğine bağlayın</div>
            </div>
            <div style={st.grid(2)}>
              {TextField({ label: 'MERKEZ KOD', k: 'centerCode', tone: 'group' })}
              {TextField({ label: 'ÖZEL KOD', k: 'specialCode', tone: 'group' })}
            </div>
          </SectionCard>

          <SectionCard
            tone="units" icon="≡" title="Birim & Barkod" subtitle="Satış birimleri ve çevrim çarpanları"
            right={!isView && <button type="button" style={{ ...btnGhost, height: 37 }} onClick={addUnit}>＋ Birim Ekle</button>}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {units.map((r, i) => (
                <div style={st.unitRow} key={i}>
                  <div style={st.field}>
                    {i === 0 && <label style={st.label}>BİRİM</label>}
                    <div style={{ position: 'relative' }}>
                      <input
                        style={{ ...boxStyle(SEC.units.field), paddingRight: 62, background: isView ? '#F7F9FF' : '#fff' }}
                        value={r.unitName} readOnly={isView}
                        placeholder={i === 0 ? 'Birim 1 *' : 'Birim'}
                        onChange={(e) => setUnit(i, 'unitName', e.target.value)}
                      />
                      {i === 0 && <span style={{ ...st.baseTag, position: 'absolute', right: 7, top: 7 }}>Ana</span>}
                    </div>
                  </div>
                  <div style={st.field}>
                    {i === 0 && <label style={st.label}>ÇARPAN</label>}
                    <input style={{ ...boxStyle(SEC.units.field), textAlign: 'right', background: isView ? '#F7F9FF' : '#fff' }} value={r.multiplier} readOnly={isView} placeholder="0,00" onChange={(e) => setUnit(i, 'multiplier', e.target.value)} />
                  </div>
                  <div style={st.field}>
                    {i === 0 && <label style={st.label}>BARKOD</label>}
                    <input style={{ ...boxStyle(SEC.units.field), fontFamily: 'ui-monospace, monospace', background: isView ? '#F7F9FF' : '#fff' }} value={r.barcode} readOnly={isView} placeholder="—" onChange={(e) => setUnit(i, 'barcode', e.target.value)} />
                  </div>
                  <div style={st.field}>
                    {i === 0 && <label style={st.label}>MAKS AĞIRLIK</label>}
                    <input style={{ ...boxStyle(SEC.units.field), textAlign: 'right', background: isView ? '#F7F9FF' : '#fff' }} value={r.maxWeight} readOnly={isView} placeholder="0,0000" onChange={(e) => setUnit(i, 'maxWeight', e.target.value)} />
                  </div>
                  <div style={st.field}>
                    {i === 0 && <label style={st.label}>&nbsp;</label>}
                    {!isView && (
                      <button
                        type="button" onClick={() => delUnit(i)} disabled={i === 0}
                        title={i === 0 ? 'Ana birim silinemez' : 'Sil'}
                        style={{ ...boxStyle(SEC.units.field), width: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: i === 0 ? 'not-allowed' : 'pointer', opacity: i === 0 ? 0.45 : 1, padding: 0 }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            tone="tax" icon="▤" title="Vergi & Ticari" subtitle="KDV oranları ve iskonto limitleri"
            note="Satış fiyatı müşteriye özel tanımlanır; ürün kartında fiyat tutulmaz."
          >
            <div style={st.grid(2)}>
              {TextField({ label: 'SATIŞ KDV ORANI', k: 'salesVatPercentage', tone: 'tax', req: true, suffix: '%', numeric: true })}
              {TextField({ label: 'ALIŞ KDV ORANI', k: 'purchaseVatPercentage', tone: 'tax', req: true, suffix: '%', numeric: true })}
              {SelectFieldV2({ label: 'VARSAYILAN ÜRÜN BİRİMİ', k: 'unit', options: DEFAULT_UNIT_OPTIONS, tone: 'tax' })}
              {TextField({ label: 'MAKS İSKONTO ORANI', k: 'maxDiscountRate', tone: 'tax', suffix: '%', numeric: true })}
              {TextField({ label: 'MAKS İSKONTO TUTARI', k: 'maxDiscountAmount', tone: 'tax', numeric: true })}
              {TextField({ label: 'ALKOL ORANI', k: 'alcoholRate', tone: 'tax', suffix: '%', numeric: true })}
              {TextField({ label: 'MİN. SİPARİŞ ADEDİ', k: 'minOrderQuantity', tone: 'tax', numeric: true, placeholder: 'Bu adedin katları kadar sipariş verilebilir — 6 girilirse 6, 12, 18… (boş = kısıt yok)' })}
              {TextField({ label: 'GTİP NO', k: 'gtip', tone: 'tax' })}
            </div>
          </SectionCard>

          <SectionCard tone="logistics" icon="⬢" title="Lojistik & Ölçüler" subtitle="Ağırlık, hacim ve fiziksel boyutlar">
            <div style={st.grid(4)}>
              {TextField({ label: 'AĞIRLIK (KG)', k: 'weight', tone: 'logistics', req: true, numeric: true })}
              {TextField({ label: 'HACİM', k: 'volume', tone: 'logistics', numeric: true })}
              {TextField({ label: 'LİTRE', k: 'liter', tone: 'logistics', numeric: true })}
              {TextField({ label: 'EN (MM)', k: 'width', tone: 'logistics', numeric: true })}
              {TextField({ label: 'BOY (MM)', k: 'lenght', tone: 'logistics', numeric: true })}
              {TextField({ label: 'YÜKSEKLİK (MM)', k: 'height', tone: 'logistics', numeric: true })}
              {TextField({ label: 'MOBİL SIRA NO', k: 'mobileSortOrder', tone: 'logistics', numeric: true, placeholder: '(opsiyonel)' })}
            </div>
          </SectionCard>

          <SectionCard tone="media" icon="▶" title="Medya" subtitle="Ürün tanıtım videosu ve görselleri (master + 2 ek)">
            {TextField({ label: 'VİDEO LİNKİ', k: 'videoUrl', tone: 'media', placeholder: "https://www.youtube.com/watch?v=… veya video URL'i" })}
            <div style={st.grid(3)}>
              <ImageBox label="MASTER GÖRSEL" value={form.productImage} onPick={pickImage('productImage')} onClear={() => set('productImage', '')} readOnly={isView} accent={C.navy} />
              <ImageBox label="EK GÖRSEL 1" value={form.additionalImage1} onPick={pickImage('additionalImage1')} onClear={() => set('additionalImage1', '')} readOnly={isView} accent={C.navy} />
              <ImageBox label="EK GÖRSEL 2" value={form.additionalImage2} onPick={pickImage('additionalImage2')} onClear={() => set('additionalImage2', '')} readOnly={isView} accent={C.navy} />
            </div>
          </SectionCard>
        </>
      )}

      {/* ── DEPOZİTOLAR ── */}
      {tab === 'Depozitolar' && (
        <SectionCard tone="deposit" icon="💰" title="Depozitolar" subtitle="Ana ürün satıldığında birlikte satılacak depozito ürünleri ve adetleri">
          {!isView && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 2, minWidth: 240 }}>
                <label style={st.label}>DEPOZİTO ÜRÜNÜ</label>
                <select
                  value={depositDraft.depositProductId}
                  onChange={(e) => setDepositDraft((d) => ({ ...d, depositProductId: e.target.value }))}
                  style={boxStyle(SEC.deposit.field)}
                >
                  <option value="">Seçiniz</option>
                  {depositProducts.filter((p) => !deposits.some((d) => d.depositProductId === p.id)).map((p) => (
                    <option key={p.id} value={p.id}>{p.name ? `${p.textId} - ${p.name}` : p.textId}</option>
                  ))}
                </select>
              </div>
              <div style={{ width: 150 }}>
                <label style={st.label}>MİKTAR</label>
                <input
                  type="number" min="0" step="any"
                  value={depositDraft.quantity}
                  onChange={(e) => setDepositDraft((d) => ({ ...d, quantity: e.target.value }))}
                  style={{ ...boxStyle(SEC.deposit.field), textAlign: 'right' }}
                />
              </div>
              <button type="button" style={btnPrimary} onClick={addDeposit}>Ekle</button>
            </div>
          )}
          {deposits.length === 0 ? (
            <div style={st.empty}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.navy }}>Henüz depozito eklenmedi</div>
              <div style={{ marginTop: 6 }}>Yukarıdan depozito ürünü ve adedini seçip “Ekle” deyin (ör. 6’lı süt → 6 şişe + 1 kasa).</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={{ ...st.th, borderRadius: '12px 0 0 0' }}>DEPOZİTO ÜRÜNÜ</th>
                    <th style={{ ...st.th, width: 180 }}>MİKTAR (ADET)</th>
                    <th style={{ ...st.th, width: 120, borderRadius: '0 12px 0 0' }}>İŞLEM</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((d) => (
                    <tr key={d.depositProductId}>
                      <td style={{ ...st.td, fontWeight: 600, color: C.navy }}>{depositLabel(d.depositProductId)}</td>
                      <td style={st.td}>
                        {isView ? d.quantity : (
                          <input
                            type="number" min="0" step="any" value={d.quantity}
                            onChange={(e) => setDepositQty(d.depositProductId, e.target.value)}
                            style={{ ...boxStyle(SEC.deposit.field), maxWidth: 140, textAlign: 'right' }}
                          />
                        )}
                      </td>
                      <td style={st.td}>
                        {!isView && <button type="button" style={btnDanger} onClick={() => removeDeposit(d.depositProductId)}>Çıkar</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {depositProducts.length === 0 && (
            <div style={st.hint}>Sistemde “Depozito” tipinde ürün yok. Önce Ürün Tipi = Depozito olan ürünler tanımlayın.</div>
          )}
        </SectionCard>
      )}

      {/* ── EK BİLGİLER ── */}
      {tab === 'Ek Bilgiler' && (
        <SectionCard tone="extra" icon="ⓘ" title="Ek Bilgiler" subtitle="Ürün özelleştirilmiş alanları">
          {fieldDefs.length === 0 ? (
            <div style={st.empty}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.navy }}>Tanımlı özelleştirilmiş alan yok</div>
              <div style={{ marginTop: 6 }}>Ürün Özelleştirilmiş Alan ekranından tanımlayabilirsiniz.</div>
            </div>
          ) : (
            <div style={st.grid(2)}>
              {fieldDefs.map((f) => {
                const invalid = tried && requiredFieldsMissing.includes(f.id) && !String(fieldValues[f.id] ?? '').trim();
                // #727 Giriş alanı tanımın veri tipine uymalı (1=Metin, 2=Sayı, 3=Tarih, 4=Evet/Hayır, 5=Liste).
                const val = fieldValues[f.id] ?? '';
                const onVal = (v) => setFieldValues((p) => ({ ...p, [f.id]: v }));
                const dt = Number(f.dataType ?? f.DataType) || 1;
                const box = { ...boxStyle(SEC.extra.field, invalid), background: isView ? '#F7F9FF' : '#fff' };
                let inputEl;
                if (isView) {
                  inputEl = <input style={box} readOnly value={val || '-'} />;
                } else if (dt === 2) {
                  inputEl = <input style={box} inputMode="decimal" value={val} onChange={(e) => onVal(sanitizeNumericInput(e.target.value))} />;
                } else if (dt === 3) {
                  inputEl = <input style={box} type="date" value={val} onChange={(e) => onVal(e.target.value)} />;
                } else if (dt === 4) {
                  inputEl = (
                    <select style={box} value={val} onChange={(e) => onVal(e.target.value)}>
                      <option value="">Seçiniz</option><option value="Evet">Evet</option><option value="Hayır">Hayır</option>
                    </select>
                  );
                } else if (dt === 5) {
                  // #743 Seçenekler [{code,description}] (geriye-uyum: eski string dizisi). Değer = KOD.
                  const opts = parseFieldOptions(f.options ?? f.Options);
                  inputEl = (
                    <select style={box} value={val} onChange={(e) => onVal(e.target.value)}>
                      <option value="">Seçiniz</option>
                      {opts.map((o) => <option key={o.code} value={o.code}>{fieldOptionLabel(o)}</option>)}
                    </select>
                  );
                } else {
                  inputEl = <input style={box} value={val} onChange={(e) => onVal(e.target.value)} />;
                }
                return (
                  <div style={st.field} key={f.id}>
                    <label style={st.label}>{f.name}{f.isRequired && <span style={st.req}>*</span>}</label>
                    {inputEl}
                    {invalid && <div style={st.errText}>Zorunlu alan</div>}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      )}

      {/* Özelleştirilmiş grup üyelik seçimi */}
      {memberModalOpen && (
        <SubModal
          title="Özelleştirilmiş Grup Havuzları"
          maxWidth={520}
          onClose={() => setMemberModalOpen(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setMemberModalOpen(false)} disabled={memberSaving}>İptal</button>
              <button className="dps-btn primary" onClick={applyMemberships} disabled={memberSaving}>{memberSaving ? 'Uygulanıyor…' : 'Uygula'}</button>
            </>
          )}
        >
          <div style={{ marginBottom: 10, fontSize: 12.5, color: C.muted }}>
            Ürünün ekleneceği havuzları işaretleyin. Seçili: {memberDraft.length} / 10
          </div>
          <input
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            placeholder="Grup kodu / adı ara…"
            style={{ ...boxStyle(SEC.group.field), marginBottom: 10, fontWeight: 500 }}
          />
          <div style={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${C.cardBorder}`, borderRadius: 8 }}>
            {(() => {
              const q = memberSearch.trim().toLocaleLowerCase('tr');
              const list = allDynamicGroups.filter((g) => !q || `${g.textId} ${g.description}`.toLocaleLowerCase('tr').includes(q));
              if (list.length === 0) return <div style={{ padding: 18, textAlign: 'center', fontSize: 13, color: C.placeholder }}>Tanımlı özelleştirilmiş grup yok.</div>;
              return list.map((g) => {
                const checked = memberDraft.includes(g.id);
                return (
                  <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 11px', borderBottom: `1px solid ${C.cardBorder}`, cursor: 'pointer', fontSize: 13.5 }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleMemberDraft(g.id)} />
                    <span style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{g.textId}</span>
                    {g.description && <span style={{ color: C.muted }}>— {g.description}</span>}
                  </label>
                );
              });
            })()}
          </div>
        </SubModal>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default ProductDetailV2Page;
